import { collection, doc, getDoc, getDocs } from "firebase/firestore";

import { checkPermission } from "../../functions/checkPermission";

const normalizeDni = (dni) => String(dni || "").trim();
const normalizeTextToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_");
const extractMemberDni = (member) => {
  if (typeof member === "string" || typeof member === "number") {
    return normalizeDni(member);
  }

  if (member && typeof member === "object") {
    return normalizeDni(member.dni || member.id || member.uid || member.userId);
  }

  return "";
};

const uniqueDnis = (values = []) => {
  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    const dni = extractMemberDni(value);
    if (!dni || seen.has(dni)) return;
    seen.add(dni);
    result.push(dni);
  });
  return result;
};

const isGlobalScopeRole = (role, usuario) => {
  const normalized = normalizeTextToken(role);
  if (
    normalized === "superadmin" ||
    normalized === "admin" ||
    normalized === "administrador" ||
    normalized === "rh" ||
    normalized === "rrhh" ||
    normalized === "asistente" ||
    normalized === "asistente_rh" ||
    normalized === "asistente_rrhh" ||
    normalized === "coordinador"
  ) {
    return true;
  }
  if (usuario && (checkPermission(usuario, "asistencia") || checkPermission(usuario, "asistencia", "APROBAR"))) {
    return true;
  }
  return false;
};

export const filtrarPorDnisPermitidos = (items = [], getDni, dnisPermitidos) => {
  if (!dnisPermitidos) return items;
  if (!(dnisPermitidos instanceof Set) || dnisPermitidos.size === 0) return [];

  return items.filter((item) => {
    const dni = normalizeDni(getDni(item));
    return dni && dnisPermitidos.has(dni);
  });
};

export const cargarAlcanceGrupoAsistencia = async ({ db, rucEmpresa, usuario }) => {
  if (!db || !rucEmpresa) {
    return {
      restringidoPorGrupo: true,
      gruposDisponibles: [],
      grupoFijoId: "todos",
      dnisPermitidos: new Set(),
      grupoAlcance: null,
    };
  }

  const gruposSnap = await getDocs(collection(db, `grupos/${String(rucEmpresa)}/grupos`));
  const gruposEmpresa = gruposSnap.docs.map((groupDoc) => {
    const data = groupDoc.data() || {};
    return {
      id: groupDoc.id,
      name: data.name || "Grupo sin nombre",
      members: uniqueDnis(data.members || []),
    };
  });

  if (isGlobalScopeRole(usuario?.rol, usuario)) {
    return {
      restringidoPorGrupo: false,
      gruposDisponibles: gruposEmpresa,
      grupoFijoId: "todos",
      dnisPermitidos: null,
      grupoAlcance: null,
    };
  }

  const dniUsuario = normalizeDni(usuario?.dni);
  let groupIds = [];

  if (dniUsuario) {
    const membershipRef = doc(db, `grupos/${String(rucEmpresa)}/memberships`, dniUsuario);
    const membershipDoc = await getDoc(membershipRef);
    if (membershipDoc.exists()) {
      const membershipData = membershipDoc.data() || {};
      const singleGroupId = String(membershipData?.groupId || "").trim();
      const multipleGroupIds = Array.isArray(membershipData?.groupIds)
        ? membershipData.groupIds.map((id) => String(id || "").trim()).filter(Boolean)
        : [];
      groupIds = Array.from(
        new Set([singleGroupId, ...multipleGroupIds].filter(Boolean))
      );
    }
  }

  const groupIdsDesdeMembers = dniUsuario
    ? gruposEmpresa
        .filter((group) => group.members.includes(dniUsuario))
        .map((group) => String(group.id))
    : [];
  const groupIdsAsignados = Array.from(
    new Set([...groupIds, ...groupIdsDesdeMembers])
  );
  const gruposAsignados = gruposEmpresa.filter((group) =>
    groupIdsAsignados.includes(String(group.id))
  );
  const dnisPermitidos = new Set(
    gruposAsignados.flatMap((group) => uniqueDnis(group.members || []))
  );

  if (gruposAsignados.length === 0) {
    return {
      restringidoPorGrupo: false,
      gruposDisponibles: gruposEmpresa,
      grupoFijoId: "todos",
      dnisPermitidos: null,
      grupoAlcance: null,
    };
  }

  return {
    restringidoPorGrupo: true,
    gruposDisponibles: gruposAsignados,
    grupoFijoId: gruposAsignados.length === 1 ? gruposAsignados[0].id : "todos",
    dnisPermitidos,
    grupoAlcance: gruposAsignados.length === 1 ? gruposAsignados[0] : null,
  };
};
